import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Globe, Bell, Moon, Sun, Shield, Lock, 
  HelpCircle, FileText, LogOut, ChevronRight,
  User, Trash2, Eye, Volume2, DollarSign, Crown, CheckCircle, Check, BellRing
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { languageNames } from '../lib/i18n';
import usePushNotifications from '../hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, token, language, setLanguage, logout, theme, toggleTheme } = useAuth();
  const navigate = useNavigate();
  const { permission, isSupported, isSubscribed, requestPermission, subscribeToPush, unsubscribe, sendTestNotification, loading: pushLoading } = usePushNotifications();
  
  const [notifications, setNotifications] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [privateAccount, setPrivateAccount] = useState(false);
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [showMonetizeModal, setShowMonetizeModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [requestingPush, setRequestingPush] = useState(false);

  useEffect(() => {
    fetchCreatorProfile();
  }, []);

  const fetchCreatorProfile = async () => {
    try {
      const response = await axios.get(`${API}/creator/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatorProfile(response.data);
      setPaypalEmail(response.data.paypal_email || '');
    } catch (error) {
      // Not a creator yet
    }
  };

  const handleEnableMonetization = async () => {
    if (!paypalEmail || !paypalEmail.includes('@')) {
      toast.error('Enter a valid PayPal address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/creator/enable`, {
        paypal_email: paypalEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Monetizasyon aktive! 🎉');
      setShowMonetizeModal(false);
      fetchCreatorProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEnablePushNotifications = async () => {
    if (permission === 'granted') {
      toast.success('Push notifications are already enabled!');
      return;
    }
    
    setRequestingPush(true);
    try {
      const granted = await requestPermission();
      if (granted) {
        toast.success('Push notifications enabled! 🔔');
        setNotifications(true);
      } else {
        toast.error('Push notifications were denied. Enable them in browser settings.');
      }
    } catch (error) {
      toast.error('Could not enable push notifications');
    } finally {
      setRequestingPush(false);
    }
  };

  const settingsSections = [
    {
      title: 'Kont',
      items: [
        {
          icon: User,
          label: 'Edit Profile',
          description: 'Chanje foto, non, byografi',
          onClick: () => navigate('/edit-profile')
        },
        {
          icon: Lock,
          label: 'Modpas',
          description: 'Chanje modpas ou',
          onClick: () => toast.info('Fonksyonalite ap vini byento')
        },
        {
          icon: Eye,
          label: 'Kont Prive',
          description: 'Only subscribers can see your posts',
          toggle: true,
          value: privateAccount,
          onChange: setPrivateAccount
        }
      ]
    },
    {
      title: 'Notifications',
      items: [
        {
          icon: BellRing,
          label: 'Enable Push Notifications',
          description: permission === 'granted' ? '✓ Enabled' : 'Get notified of new activity',
          hasChevron: permission !== 'granted',
          onClick: permission !== 'granted' ? handleEnablePushNotifications : null,
          disabled: requestingPush
        },
        {
          icon: BellRing,
          label: 'Push Notifications',
          description: isSupported ? (isSubscribed ? 'Enabled' : (permission === 'denied' ? 'Blocked by browser' : 'Disabled')) : 'Not supported',
          toggle: isSupported && permission !== 'denied',
          value: isSubscribed,
          onChange: async () => {
            if (isSubscribed) {
              await unsubscribe();
              toast.success('Push notifications disabled');
            } else {
              if (permission !== 'granted') {
                const granted = await requestPermission();
                if (!granted) {
                  toast.error('Please enable notifications in your browser settings');
                  return;
                }
              }
              await subscribeToPush();
              toast.success('Push notifications enabled!');
            }
          }
        },
        {
          icon: Bell,
          label: 'In-App Notifications',
          description: 'Show notification badge',
          toggle: true,
          value: notifications,
          onChange: setNotifications
        },
        {
          icon: Volume2,
          label: 'Sound',
          description: 'Play sound for messages',
          toggle: true,
          value: soundEnabled,
          onChange: setSoundEnabled
        }
      ]
    },
    {
      title: 'Aparans',
      items: [
        {
          icon: Globe,
          label: 'Lang / Language',
          description: languageNames[language] || 'English',
          hasChevron: true,
          onClick: () => setShowLanguageModal(true)
        },
        {
          icon: theme === 'dark' ? Moon : Sun,
          label: 'Theme',
          description: theme === 'dark' ? 'Dark Mode' : 'Light Mode',
          toggle: true,
          value: theme === 'dark',
          onChange: toggleTheme
        }
      ]
    },
    {
      title: 'Support & Legal',
      items: [
        {
          icon: FileText,
          label: 'Rules & Guidelines',
          description: 'Community rules',
          onClick: () => navigate('/rules')
        },
        {
          icon: Shield,
          label: 'Privacy',
          description: 'Privacy policy',
          onClick: () => navigate('/privacy')
        },
        {
          icon: HelpCircle,
          label: 'Help & Support',
          description: 'Contact support',
          onClick: () => navigate('/support')
        }
      ]
    }
  ];

  return (
    <div className="max-w-2xl mx-auto pb-20" data-testid="settings-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-bold text-lg">Reglaj</h1>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {/* User Info */}
        <div className="flex items-center gap-4 p-4 bg-secondary/30 rounded-2xl">
          <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xl font-bold">
            {user?.display_name?.[0] || user?.username?.[0] || 'U'}
          </div>
          <div className="flex-1">
            <p className="font-semibold">{user?.display_name || user?.username}</p>
            <p className="text-sm text-muted-foreground">@{user?.username}</p>
          </div>
          <Link to="/edit-profile">
            <Button variant="outline" size="sm" className="rounded-full">
              Modifye
            </Button>
          </Link>
        </div>

        {/* Settings Sections */}
        {settingsSections.map((section, sIndex) => (
          <div key={sIndex} className="space-y-2">
            <h2 className="text-sm font-semibold text-muted-foreground px-2">{section.title}</h2>
            <div className="bg-secondary/20 rounded-2xl overflow-hidden divide-y divide-border/30">
              {section.items.map((item, iIndex) => (
                <div
                  key={iIndex}
                  className={`flex items-center gap-4 p-4 ${!item.toggle ? 'cursor-pointer hover:bg-secondary/30 transition-colors' : ''}`}
                  onClick={!item.toggle ? item.onClick : undefined}
                  data-testid={`setting-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                  {item.toggle ? (
                    <Switch
                      checked={item.value}
                      onCheckedChange={item.onChange}
                    />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Logout & Delete Account */}
        <div className="space-y-3 pt-4">
          <Button 
            variant="outline" 
            className="w-full rounded-full h-12 text-destructive border-destructive/50 hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Dekonekte
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full rounded-full h-12 text-destructive hover:bg-destructive/10"
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-5 h-5 mr-2" />
                Delete My Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete your account and all your data.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Anile</AlertDialogCancel>
                <AlertDialogAction 
                  className="rounded-full bg-destructive hover:bg-destructive/90"
                                  onClick={() => toast.info('Contact support to delete account: fityhapp@gmail.com')}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* App Version */}
        <div className="text-center pt-4">
          <p className="text-sm text-muted-foreground">FityH v1.0.0</p>
          <p className="text-xs text-muted-foreground mt-1">© 2026 FityH. All rights reserved.</p>
        </div>
      </div>

      {/* Language Selection Modal */}
      <Dialog open={showLanguageModal} onOpenChange={setShowLanguageModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Select Language / Chwazi Lang
            </DialogTitle>
            <DialogDescription>
              Choose your preferred language for the app
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {Object.entries(languageNames).map(([code, name]) => (
              <button
                key={code}
                onClick={async () => {
                  setLanguage(code);
                  setShowLanguageModal(false);
                  toast.success(`Language changed to ${name}`);
                  // Save language preference to backend
                  try {
                    await axios.put(`${API}/auth/profile`, { language: code }, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                  } catch (e) {
                    console.error('Failed to save language preference:', e);
                  }
                }}
                className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                  language === code 
                    ? 'bg-primary/20 border-2 border-primary' 
                    : 'bg-secondary/30 hover:bg-secondary/50 border-2 border-transparent'
                }`}
                data-testid={`lang-${code}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {code === 'en' ? '🇺🇸' : code === 'ht' ? '🇭🇹' : code === 'fr' ? '🇫🇷' : '🇪🇸'}
                  </span>
                  <span className="font-medium">{name}</span>
                </div>
                {language === code && (
                  <Check className="w-5 h-5 text-primary" />
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
